import { useTranslation } from 'react-i18next';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { Mail, Phone, MapPin, User, Briefcase } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { FieldGroup, FieldSet, FieldLegend, FieldSeparator } from '@/components/ui/field';
import { FormField } from '@/shared/ui';

import { CONTACT_FIELD_MAX_LENGTH as MAX, type ContactFormValues } from '../model';

interface ContactFormFieldsProps {
  register: UseFormRegister<ContactFormValues>;
  errors: FieldErrors<ContactFormValues>;
  isViewMode: boolean;
}

export const ContactFormFields = ({ register, errors, isViewMode }: ContactFormFieldsProps) => {
  const { t } = useTranslation();

  return (
    <FieldGroup>
      <FieldSet>
        <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
          <User className="w-4 h-4" />
          {t('contacts.sections.identity')}
        </FieldLegend>
        <FieldSeparator />
        <FieldGroup className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('contact.title.label')} name="title" error={errors.title?.message}>
            <Input
              id="title"
              {...register('title')}
              maxLength={MAX.title}
              placeholder={t('contact.title.desc')}
              disabled={isViewMode}
            />
          </FormField>
          <FormField label={t('contact.name.label')} name="name" error={errors.name?.message} required>
            <Input
              id="name"
              {...register('name')}
              maxLength={MAX.name}
              placeholder={t('contact.name.desc')}
              disabled={isViewMode}
            />
          </FormField>
          <FormField
            label={t('contact.job.title.label')}
            name="jobTitle"
            error={errors.jobTitle?.message}
            className="md:col-span-2">
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="jobTitle"
                {...register('jobTitle')}
                maxLength={MAX.jobTitle}
                className="pl-10"
                placeholder={t('contact.job.title.desc')}
                disabled={isViewMode}
              />
            </div>
          </FormField>
        </FieldGroup>
      </FieldSet>

      <FieldSet>
        <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
          <MapPin className="w-4 h-4" />
          {t('contact.location.label')}
        </FieldLegend>
        <FieldSeparator />
        <FormField label={t('contact.address.label')} name="address" error={errors.address?.message}>
          <Input
            id="address"
            {...register('address')}
            maxLength={MAX.address}
            placeholder={t('contact.address.desc')}
            disabled={isViewMode}
          />
        </FormField>
      </FieldSet>

      <FieldSet>
        <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
          <Mail className="w-4 h-4" />
          {t('contacts.sections.communication')}
        </FieldLegend>
        <FieldSeparator />
        <FieldGroup className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label={t('contact.email1.label')} name="email1" error={errors.email1?.message}>
            <Input
              id="email1"
              type="email"
              {...register('email1')}
              maxLength={MAX.email}
              placeholder={t('contact.email.desc')}
              disabled={isViewMode}
            />
          </FormField>
          <FormField label={t('contact.email2.label')} name="email2" error={errors.email2?.message}>
            <Input
              id="email2"
              type="email"
              {...register('email2')}
              maxLength={MAX.email}
              placeholder={t('contact.email.desc')}
              disabled={isViewMode}
            />
          </FormField>
          <FormField label={t('contact.email3.label')} name="email3" error={errors.email3?.message}>
            <Input
              id="email3"
              type="email"
              {...register('email3')}
              maxLength={MAX.email}
              placeholder={t('contact.email.desc')}
              disabled={isViewMode}
            />
          </FormField>
        </FieldGroup>
      </FieldSet>

      <FieldSet>
        <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
          <Phone className="w-4 h-4" />
          {t('contacts.sections.telephony')}
        </FieldLegend>
        <FieldSeparator />
        <FieldGroup className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label={t('contact.phone1.label')} name="phone1" error={errors.phone1?.message}>
            <Input
              id="phone1"
              {...register('phone1')}
              maxLength={MAX.phone}
              placeholder={t('contact.phone1.desc')}
              disabled={isViewMode}
            />
          </FormField>
          <FormField label={t('contact.phone2.label')} name="phone2" error={errors.phone2?.message}>
            <Input
              id="phone2"
              {...register('phone2')}
              maxLength={MAX.phone}
              placeholder={t('contact.phone.desc')}
              disabled={isViewMode}
            />
          </FormField>
          <FormField label={t('contact.phone3.label')} name="phone3" error={errors.phone3?.message}>
            <Input
              id="phone3"
              {...register('phone3')}
              maxLength={MAX.phone}
              placeholder={t('contact.phone.desc')}
              disabled={isViewMode}
            />
          </FormField>
        </FieldGroup>
      </FieldSet>
    </FieldGroup>
  );
};
